/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PhoneEntryView from '../views/login/password/phoneInputView';
import SmsCodeEntryView from '../views/login/password/smsCodeInputView';
import UserDetailsView from '../views/login/password/userDetailsFormView';
import { ScreenContextProvider } from './screenContext';
import Navbar from '../components/navbar';
import Screen from '../views/screen/screen';
import useSessionStorage from '../hooks/useSessionStorage';
import request from '../utils/request';
import useRequestError from '../hooks/useRequestError';

export interface IUserDetailsForm {
  title: string;
  fields: Array<{
    id: string;
    type: string;
    placeholder: string;
    value: string;
    required: boolean;
    selected: boolean;
  }>;
}

export interface IDeviceRegistrationForm {
  defaultCountryCode: string;
  userDetailsForm: IUserDetailsForm;
}

interface IPasswordLoginContext {
  phone: {
    countryCode: string;
    phoneNumber: string;
  };
  // What is in the code field right now.
  smsCode: string;
  // The code the backend has already accepted. The details form replays it to
  // finish a new signup, so it outlives the field it was typed into.
  verifiedCode: string;
  deviceRegistrationForm?: IDeviceRegistrationForm;
  setPhone: (countryCode: string, phoneNumber: string) => void;
  setSmsCode: (smsCode: string) => void;
  setVerifiedCode: (verifiedCode: string) => void;
}

export interface IPasswordLoginSource {
  viewType?:
    | 'PHONE_ENTRY'
    | 'SMS_CODE_ENTRY'
    | 'PASSWORD_ENTRY'
    | 'USER_DETAILS';
  screenResource?: string;
}

interface ILoginState {
  phone: {
    countryCode: string;
    phoneNumber: string;
  };
  smsCode: string;
  verifiedCode: string;
  deviceRegistrationForm?: IDeviceRegistrationForm;
}

// What a reload or a back navigation restores. `smsCode` is deliberately
// absent: restoring the field would hand the code screen a complete code
// nobody typed, and it would be sent again. `verifiedCode` is safe to keep --
// nothing submits it on sight, and the details form needs it to finish a
// signup the patient reloaded partway through.
type ICachedLoginState = Pick<ILoginState, 'phone' | 'verifiedCode'>;

export const PasswordLoginContext = React.createContext(
  {} as IPasswordLoginContext
);

interface IPasswordLoginContextProvider {
  routeSource?: IPasswordLoginSource;
}

export const PasswordLoginContextProvider = ({
  routeSource,
}: IPasswordLoginContextProvider) => {
  const [getCache, setCache] = useSessionStorage<ICachedLoginState>(
    'PASSWORD_LOGIN_CONTEXT'
  );
  const setCacheRef = useRef(setCache);
  const getCacheRef = useRef(getCache);

  const handleRequestErrorRef = useRef(useRequestError());

  const [state, setState] = useState<ILoginState>({
    phone: getCacheRef.current()?.phone || {
      countryCode: '',
      phoneNumber: '',
    },
    smsCode: '',
    verifiedCode: getCacheRef.current()?.verifiedCode || '',
  });

  useEffect(() => {
    // This request fetches the current market country calling code
    // and form config used to fill user details later in login flow
    request<IDeviceRegistrationForm>('/api/view/device_registration_form')
      .then((res) => {
        setState((prevState) => {
          // Keep the same `phone` when there is nothing to fill in. A fresh
          // object re-runs every effect watching it, and on the code screen
          // that means re-submitting a code the patient already sent.
          if (prevState.phone.countryCode) {
            return { ...prevState, deviceRegistrationForm: res };
          }

          return {
            ...prevState,
            phone: {
              ...prevState.phone,
              countryCode: res.defaultCountryCode,
            },
            deviceRegistrationForm: res,
          };
        });
      })
      .catch((e) => {
        handleRequestErrorRef.current(e);
      });
  }, []);

  useEffect(() => {
    setCacheRef.current({
      phone: state.phone,
      verifiedCode: state.verifiedCode,
    });
  }, [state.phone, state.verifiedCode]);

  const setPhone = useCallback((countryCode: string, phoneNumber: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        phone: {
          countryCode,
          phoneNumber,
        },
      };
    });
  }, []);

  const setSmsCode = useCallback((smsCode: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        smsCode,
      };
    });
  }, []);

  const setVerifiedCode = useCallback((verifiedCode: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        verifiedCode,
      };
    });
  }, []);

  const getView = () => {
    switch (routeSource?.viewType) {
      case 'SMS_CODE_ENTRY':
        return <SmsCodeEntryView />;
      case 'USER_DETAILS':
        return <UserDetailsView />;
      case 'PASSWORD_ENTRY':
        // we get VL screen url to set and verify password
        return (
          <ScreenContextProvider
            routeSource={{ url: routeSource.screenResource }}
          >
            {/* Navbar */}
            <Navbar />

            <Screen visibleNavbar />
          </ScreenContextProvider>
        );
      // Password login default screen
      case 'PHONE_ENTRY':
      default:
        return <PhoneEntryView />;
    }
  };

  return (
    <PasswordLoginContext.Provider
      value={{
        ...state,
        setPhone,
        setSmsCode,
        setVerifiedCode,
      }}
    >
      {getView()}
    </PasswordLoginContext.Provider>
  );
};
